import * as React from 'react';
import { PageSection, Title, Card, CardBody, Spinner, Alert, Button, Pagination, PaginationVariant, Toolbar, ToolbarContent, ToolbarItem, ToolbarToggleGroup, ToolbarGroup, ToolbarFilter, SearchInput, MenuToggle, Menu, MenuContent, MenuList, MenuItem, Popper, EmptyState, EmptyStateBody, Bullseye, Select, SelectList, SelectOption, OverflowMenu, OverflowMenuContent, OverflowMenuControl, OverflowMenuGroup, OverflowMenuItem, OverflowMenuDropdownItem, Dropdown, DropdownList } from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, Td, SortByDirection, ISortBy, OuterScrollContainer, InnerScrollContainer } from '@patternfly/react-table';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { buildTimeBranding, useSiteConfig } from '../config/SiteConfigContext';
import { listAccessibleFiles, readFileContents, S3File, formatRequestOwner } from '../utils/s3Utils';
import { useEffect, useState, useRef, useMemo } from 'react';
import { Label } from '@patternfly/react-core';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FilterIcon, EllipsisVIcon } from '@patternfly/react-icons';
import {
  ReportDataTable,
  ReportDonut,
  ReportInfoCard,
  coverageSlices,
  prioritySlices,
} from './ReportOverview';

const REQUEST_LIST_FILTER_COLUMNS = ['Type of Check', 'Initiated by', 'Request time', 'Status'] as const;

const getRequestListCellText = (file: S3File, column: string): string => {
  switch (column) {
    case 'Type of Check':
      return file.reportName || '';
    case 'Initiated by':
      return formatRequestOwner(file);
    case 'Request time':
      return Number.isNaN(file.lastModified.getTime()) ? '' : file.lastModified.toLocaleString();
    case 'Status': {
      const normalized = (file.state || '').toLowerCase();
      if (normalized === 'report ready') {
        return 'Report ready';
      }
      if (normalized === 'analysing data') {
        return 'Analysing data';
      }
      return 'Collecting data';
    }
    default:
      return '';
  }
};

const getRequestListSortValue = (file: S3File, columnIndex: number): string | number => {
  if (columnIndex === 2) {
    const time = file.lastModified.getTime();
    return Number.isNaN(time) ? 0 : time;
  }
  if (columnIndex === 3) {
    const normalized = (file.state || '').toLowerCase();
    if (normalized === 'report ready') {
      return 2;
    }
    if (normalized === 'analysing data') {
      return 1;
    }
    return 0;
  }
  return getRequestListCellText(file, REQUEST_LIST_FILTER_COLUMNS[columnIndex] || '').toLowerCase();
};

const RequestListActions: React.FunctionComponent<{
  file: S3File;
  onViewReport: () => void;
  onStatusLogs: () => void;
}> = ({ file, onViewReport, onStatusLogs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const canViewReport = (file.state || '').toLowerCase() === 'report ready';
  const overflowItems = [
    ...(canViewReport
      ? [{ key: 'view-report', label: 'View report', onClick: onViewReport }]
      : []),
    { key: 'status-logs', label: 'Status logs', onClick: onStatusLogs }
  ];

  return (
    <OverflowMenu breakpoint="lg">
      <OverflowMenuContent>
        <OverflowMenuGroup groupType="button">
          {overflowItems.map((item) => (
            <OverflowMenuItem key={item.key}>
              <Button variant="primary" onClick={item.onClick}>
                {item.label}
              </Button>
            </OverflowMenuItem>
          ))}
        </OverflowMenuGroup>
      </OverflowMenuContent>
      <OverflowMenuControl>
        <Dropdown
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          onSelect={() => setIsOpen(false)}
          toggle={(toggleRef) => (
            <MenuToggle
              ref={toggleRef}
              aria-label={`Actions for ${file.reportName}`}
              variant="plain"
              onClick={() => setIsOpen((open) => !open)}
              isExpanded={isOpen}
              icon={<EllipsisVIcon />}
            />
          )}
        >
          <DropdownList>
            {overflowItems.map((item) => (
              <OverflowMenuDropdownItem key={item.key} isShared onClick={item.onClick}>
                {item.label}
              </OverflowMenuDropdownItem>
            ))}
          </DropdownList>
        </Dropdown>
      </OverflowMenuControl>
    </OverflowMenu>
  );
};

const Requests: React.FunctionComponent = () => {
  const site = useSiteConfig();
  useDocumentTitle(
    `${site?.siteId || buildTimeBranding.siteId || 'QuickCheck'} ${site?.productName || buildTimeBranding.applicationName || ''} | Requests`
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState<S3File[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listPerPage, setListPerPage] = useState(10);
  const [listSortBy, setListSortBy] = useState<ISortBy>({ index: 2, direction: SortByDirection.desc });
  const [listFilters, setListFilters] = useState<{ [key: string]: string }>({});
  const [listSearchColumn, setListSearchColumn] = useState<string>('Type of Check');
  const [listSearchValue, setListSearchValue] = useState('');
  const [listAttrOpen, setListAttrOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [reportInfo, setReportInfo] = useState<any>(null);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [paginatedData, setPaginatedData] = useState<any[]>([]);
  const [activeAccountsSortBy, setActiveAccountsSortBy] = useState<ISortBy>({ index: 0, direction: SortByDirection.asc });
  const [accountFindingsSortBy, setAccountFindingsSortBy] = useState<ISortBy>({ index: 0, direction: SortByDirection.asc });
  const [environmentFindingsSortBy, setEnvironmentFindingsSortBy] = useState<ISortBy>({ index: 0, direction: SortByDirection.asc });
  const [assetFindingsSortBy, setAssetFindingsSortBy] = useState<ISortBy>({ index: 0, direction: SortByDirection.asc });
  const [assetsSortBy, setAssetsSortBy] = useState<ISortBy>({ index: 0, direction: SortByDirection.asc });
  const [activeAccountsSearchValue, setActiveAccountsSearchValue] = useState('');
  const [accountFindingsSearchValue, setAccountFindingsSearchValue] = useState('');
  const [environmentFindingsSearchValue, setEnvironmentFindingsSearchValue] = useState('');
  const [assetFindingsSearchValue, setAssetFindingsSearchValue] = useState('');
  const [assetsSearchValue, setAssetsSearchValue] = useState('');

  const [activeAccountsSearchColumn, setActiveAccountsSearchColumn] = useState<string>('Source');
  const [accountFindingsSearchColumn, setAccountFindingsSearchColumn] = useState<string>('Source');
  const [environmentFindingsSearchColumn, setEnvironmentFindingsSearchColumn] = useState<string>('Source');
  const [assetFindingsSearchColumn, setAssetFindingsSearchColumn] = useState<string>('Source');
  const [assetsSearchColumn, setAssetsSearchColumn] = useState<string>('Source');

  const [activeAccountsSelectOpen, setActiveAccountsSelectOpen] = useState(false);
  const [accountFindingsSelectOpen, setAccountFindingsSelectOpen] = useState(false);
  const [environmentFindingsSelectOpen, setEnvironmentFindingsSelectOpen] = useState(false);
  const [assetFindingsSelectOpen, setAssetFindingsSelectOpen] = useState(false);
  const [assetsSelectOpen, setAssetsSelectOpen] = useState(false);

  // Add state for multiple filters
  const [activeAccountsFilters, setActiveAccountsFilters] = useState<{ [key: string]: string }>({});
  const [accountFindingsFilters, setAccountFindingsFilters] = useState<{ [key: string]: string }>({});
  const [environmentFindingsFilters, setEnvironmentFindingsFilters] = useState<{ [key: string]: string }>({});
  const [assetFindingsFilters, setAssetFindingsFilters] = useState<{ [key: string]: string }>({});
  const [assetsFilters, setAssetsFilters] = useState<{ [key: string]: string }>({});

  const isReportReady = (state?: string) => (state || '').toLowerCase() === 'report ready';
  const normalizedReportType = String(reportInfo?.reportType || '').toLowerCase();
  const selectedReportCard = selectedFile ? files.find(file => file.filePath === selectedFile) : undefined;
  const selectedReportTime = selectedReportCard?.lastModified?.toLocaleString();

  const getRequestStatusLabel = (state?: string): { text: string; status: 'success' | 'info' } => {
    const normalized = (state || '').toLowerCase();
    if (normalized === 'report ready') {
      return { text: 'Report ready', status: 'success' };
    }
    if (normalized === 'analysing data') {
      return { text: 'Analysing data', status: 'info' };
    }
    return { text: 'Collecting data', status: 'info' };
  };

  const filteredListFiles = useMemo(() => {
    const activeFilters = Object.entries(listFilters).filter(([, value]) => value.trim());
    let rows = [...files];
    if (activeFilters.length > 0) {
      rows = rows.filter((file) =>
        activeFilters.every(([column, value]) =>
          getRequestListCellText(file, column).toLowerCase().includes(value.trim().toLowerCase())
        )
      );
    }

    const columnIndex = listSortBy.index ?? 2;
    const direction = listSortBy.direction || SortByDirection.desc;
    rows.sort((a, b) => {
      const aValue = getRequestListSortValue(a, columnIndex);
      const bValue = getRequestListSortValue(b, columnIndex);
      let cmp = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        cmp = aValue - bValue;
      } else {
        cmp = String(aValue).localeCompare(String(bValue));
      }
      return direction === SortByDirection.asc ? cmp : -cmp;
    });
    return rows;
  }, [files, listFilters, listSortBy]);

  const paginatedListFiles = useMemo(() => {
    const start = (listPage - 1) * listPerPage;
    return filteredListFiles.slice(start, start + listPerPage);
  }, [filteredListFiles, listPage, listPerPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredListFiles.length / listPerPage) || 1);
    if (listPage > maxPage) {
      setListPage(maxPage);
    }
  }, [filteredListFiles.length, listPage, listPerPage]);

  // Update initial state to use first column instead of 'all'
  const [activeAttributeMenu, setActiveAttributeMenu] = useState<string>('Source');
  const [isAttributeMenuOpen, setIsAttributeMenuOpen] = useState(false);
  const attributeToggleRef = useRef<HTMLButtonElement>(null);
  const attributeMenuRef = useRef<HTMLDivElement>(null);
  const attributeContainerRef = useRef<HTMLDivElement>(null);

  // Add effect for attribute menu keyboard and click handling
  useEffect(() => {
    const handleAttributeMenuKeys = (event: KeyboardEvent) => {
      if (!isAttributeMenuOpen) {
        return;
      }
      if (
        attributeMenuRef.current?.contains(event.target as Node) ||
        attributeToggleRef.current?.contains(event.target as Node)
      ) {
        if (event.key === 'Escape' || event.key === 'Tab') {
          setIsAttributeMenuOpen(!isAttributeMenuOpen);
          attributeToggleRef.current?.focus();
        }
      }
    };

    const handleAttributeClickOutside = (event: MouseEvent) => {
      if (isAttributeMenuOpen && !attributeMenuRef.current?.contains(event.target as Node)) {
        setIsAttributeMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleAttributeMenuKeys);
    window.addEventListener('click', handleAttributeClickOutside);
    return () => {
      window.removeEventListener('keydown', handleAttributeMenuKeys);
      window.removeEventListener('click', handleAttributeClickOutside);
    };
  }, [isAttributeMenuOpen]);

  const onAttributeToggleClick = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    setTimeout(() => {
      if (attributeMenuRef.current) {
        const firstElement = attributeMenuRef.current.querySelector('li > button:not(:disabled)');
        firstElement && (firstElement as HTMLElement).focus();
      }
    }, 0);
    setIsAttributeMenuOpen(!isAttributeMenuOpen);
  };

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const accessibleFiles = await listAccessibleFiles();
        setFiles(accessibleFiles);

        // Check for URL parameters
        const requestId = searchParams.get('requestId');

        // If requestId parameter is present, try to load the specific report
        if (requestId) {
          const matchingFile = accessibleFiles.find(file => file.filePath === requestId);
          
          if (matchingFile) {
            if (isReportReady(matchingFile.state)) {
              await handleFileClick(requestId);
            } else {
              navigate(`/status?requestId=${encodeURIComponent(matchingFile.requestId || requestId)}`);
            }
          } else {
            setError(`Report not found for requestId: ${requestId}`);
          }
        } else {
          // No requestId in URL means list view.
          setSelectedFile(null);
          setFileData([]);
          setReportInfo(null);
          setPage(1);
        }
      } catch (err: any) {
        console.error('Error in Requests component:', err);
        console.error('Error details:', {
          name: err?.name,
          message: err?.message,
          stack: err?.stack
        });
        setError('Failed to load files. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [searchParams]);

  useEffect(() => {
    if (fileData.length > 0) {
      let filteredData = [...fileData];
      
      // Apply search filtering based on report type from report metadata
      if (selectedFile) {
        const filterData = (item: any, filters: { [key: string]: string }) => {
          return Object.entries(filters).every(([column, searchValue]) => {
            if (!searchValue) return true;
            
            // Map UI column names to actual data field names
            const columnMapping: { [key: string]: string } = {
              'MFA Status': 'MFAStatus',
              'Last Activity': 'LastActivity',
              'Privileged Roles': 'PrivilegedRoles',
              'Finding Name': 'FindingName',
              'Suggested Remediation': 'SuggestedRemediation',
              'IP Address': 'IPAddress',
              'OS Type': 'OSType',
              'OS Name': 'OSName',
              'OS Version': 'OSVersion',
              'Last Seen': 'LastSeen',
              'Defender Coverage': 'DefenderCoverage',
              'Intune Coverage': 'IntuneCoverage'
            };

            const actualColumn = columnMapping[column] || column;
            const value = item[actualColumn];
            return value !== undefined && String(value).toLowerCase().includes(searchValue.toLowerCase());
          });
        };

        if (normalizedReportType.includes('active accounts')) {
          filteredData = fileData.filter(item => filterData(item, activeAccountsFilters));
        } else if (normalizedReportType.includes('account findings')) {
          filteredData = fileData.filter(item => filterData(item, accountFindingsFilters));
        } else if (normalizedReportType.includes('environment findings')) {
          filteredData = fileData.filter(item => filterData(item, environmentFindingsFilters));
        } else if (normalizedReportType.includes('asset findings')) {
          filteredData = fileData.filter(item => filterData(item, assetFindingsFilters));
        } else if (normalizedReportType.includes('detected assets')) {
          filteredData = fileData.filter(item => filterData(item, assetsFilters));
        }
      }

      const startIdx = (page - 1) * perPage;
      const endIdx = startIdx + perPage;
      setPaginatedData(filteredData.slice(startIdx, endIdx));
    }
  }, [fileData, page, perPage, selectedFile, normalizedReportType,
      activeAccountsFilters, accountFindingsFilters, environmentFindingsFilters, 
      assetFindingsFilters, assetsFilters]);

  const navigateToStatus = (requestId: string) => {
    navigate(`/status?requestId=${encodeURIComponent(requestId)}`);
  };

  const handleFileClick = async (filePath: string) => {
    try {
      const selected = files.find(file => file.filePath === filePath);
      if (selected && !isReportReady(selected.state)) {
        navigateToStatus(selected.requestId || filePath);
        return;
      }

      // Avoid a second fetch/loading flash when URL params update after the first load
      if (selectedFile === filePath && reportInfo) {
        return;
      }

      setReportLoading(true);
      setError(null);

      const response = await readFileContents(filePath);
      const data = response.reportData;
      setFileData(data);
      setReportInfo(response.reportInfo);
      setSelectedFile(filePath);
      setPage(1); // Reset to first page when loading new data

      // Reset search values and columns when switching files
      setActiveAccountsSearchValue('');
      setAccountFindingsSearchValue('');
      setEnvironmentFindingsSearchValue('');
      setAssetFindingsSearchValue('');
      setAssetsSearchValue('');
      
      // Reset column selections to 'Source'
      setActiveAccountsSearchColumn('Source');
      setAccountFindingsSearchColumn('Source');
      setEnvironmentFindingsSearchColumn('Source');
      setAssetFindingsSearchColumn('Source');
      setAssetsSearchColumn('Source');
      setActiveAttributeMenu('Source');

      // Reset filters
      setActiveAccountsFilters({});
      setAccountFindingsFilters({});
      setEnvironmentFindingsFilters({});
      setAssetFindingsFilters({});
      setAssetsFilters({});

      // Update URL parameters
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('requestId', filePath);
      navigate(`/requests?${newSearchParams.toString()}`);
    } catch (err: any) {
      console.error('Error reading file:', err);
      setError('Failed to read file contents. Please try again later.');
      setSelectedFile(null);
      setFileData([]);
      setReportInfo(null);
    } finally {
      setReportLoading(false);
    }
  };

  const handleBackClick = () => {
    setSelectedFile(null);
    setFileData([]);
    setReportInfo(null);
    setReportLoading(false);
    setPage(1);
    // Reset search values and columns when going back
    setActiveAccountsSearchValue('');
    setAccountFindingsSearchValue('');
    setEnvironmentFindingsSearchValue('');
    setAssetFindingsSearchValue('');
    setAssetsSearchValue('');
    setActiveAccountsSearchColumn('Source');
    setAccountFindingsSearchColumn('Source');
    setEnvironmentFindingsSearchColumn('Source');
    setAssetFindingsSearchColumn('Source');
    setAssetsSearchColumn('Source');
    setActiveAttributeMenu('Source');
    // Reset filters
    setActiveAccountsFilters({});
    setAccountFindingsFilters({});
    setEnvironmentFindingsFilters({});
    setAssetFindingsFilters({});
    setAssetsFilters({});
    // Clear URL parameters by navigating to the base requests path
    navigate('/requests');
  };

  const handleSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number
  ) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const handleListSetPage = (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number
  ) => {
    setListPage(newPage);
  };

  const handleListPerPageSelect = (
    _event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number
  ) => {
    setListPerPage(newPerPage);
    setListPage(newPage);
  };

  const renderListPagination = () => (
    <Pagination
      itemCount={filteredListFiles.length}
      page={listPage}
      perPage={listPerPage}
      onSetPage={handleListSetPage}
      onPerPageSelect={handleListPerPageSelect}
      variant={PaginationVariant.top}
      titles={{
        paginationAriaLabel: 'Requests pagination'
      }}
    />
  );

  const handleListSearchChange = (value: string) => {
    setListSearchValue(value);
    setListPage(1);
    setListFilters((prev) => {
      const next = { ...prev };
      if (value.trim()) {
        next[listSearchColumn] = value.trim();
      } else {
        delete next[listSearchColumn];
      }
      return next;
    });
  };

  const clearListFilters = () => {
    setListFilters({});
    setListSearchValue('');
    setListPage(1);
  };

  const renderPagination = (variant: string) => (
    <Pagination
      itemCount={fileData.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'TRUE':
        return <Label color="green">{labelText}</Label>;
      case 'FALSE':
        return <Label color="red">{labelText}</Label>;
      default:
        return <></>;
    }
  };

  const renderPriority = (priority: number) => {
    if (priority > 8) {
      return <Label color="red">{priority}</Label>;
    } else if (priority > 5) {
      return <Label color="orange">{priority}</Label>;
    } else {
      return <Label color="green">{priority}</Label>;
    }
  };

  const onSort = (
    _event: React.MouseEvent,
    index: number,
    direction: SortByDirection,
    setSortBy: React.Dispatch<React.SetStateAction<ISortBy>>
  ) => {
    setSortBy({ index, direction });
    const sortedData = [...fileData].sort((a, b) => {
      // Special handling for Priority column (index 1 in Account, Asset, and Environment Findings tables)
      if ((selectedFile?.includes('Account Findings') || selectedFile?.includes('Asset Findings') || selectedFile?.includes('Environment Findings')) && index === 1) {
        const aValue = a.Priority;
        const bValue = b.Priority;
        return direction === SortByDirection.asc
          ? aValue - bValue
          : bValue - aValue;
      }

      // Special handling for Defender and Intune coverage columns (indices 7 and 8 in Detected Assets table)
      if (selectedFile?.includes('Detected Assets') && (index === 7 || index === 8)) {
        const columnKey = index === 7 ? 'DefenderCoverage' : 'IntuneCoverage';
        const aValue = a[columnKey];
        const bValue = b[columnKey];
        // Sort TRUE before FALSE
        if (aValue === 'TRUE' && bValue === 'FALSE') {
          return direction === SortByDirection.asc ? -1 : 1;
        }
        if (aValue === 'FALSE' && bValue === 'TRUE') {
          return direction === SortByDirection.asc ? 1 : -1;
        }
        return 0;
      }

      const columnKey = Object.keys(a)[index];
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === SortByDirection.asc
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === SortByDirection.asc
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });

    setFileData(sortedData);
  };

  const activeAccountsColumns = [
    { name: 'Source', width: '10%' },
    { name: 'First Name', width: '15%' },
    { name: 'Last Name', width: '15%' },
    { name: 'Email', width: '20%' },
    { name: 'Last Activity', width: '15%' },
    { name: 'MFA Status', width: '10%' },
    { name: 'Privileged Roles', width: '15%' }
  ];

  const accountFindingsColumns = [
    { name: 'Source', width: '10%' },
    { name: 'Priority', width: '10%' },
    { name: 'Finding Name', width: '20%' },
    { name: 'Resource', width: '20%' },
    { name: 'Description', width: '20%' },
    { name: 'Suggested Remediation', width: '20%' }
  ];

  const environmentFindingsColumns = [
    { name: 'Source', width: '10%' },
    { name: 'Priority', width: '10%' },
    { name: 'Finding Name', width: '15%' },
    { name: 'Resource', width: '10%' },
    { name: 'Description', width: '20%' },
    { name: 'Remediation', width: '20%' },
    { name: 'More Info', width: '15%' }
  ];

  const assetFindingsColumns = [
    { name: 'Source', width: '10%' },
    { name: 'Priority', width: '10%' },
    { name: 'Finding Name', width: '15%' },
    { name: 'Resource', width: '15%' },
    { name: 'Category', width: '10%' },
    { name: 'Description', width: '20%' },
    { name: 'Suggested Remediation', width: '20%' }
  ];

  const assetsColumns = [
    { name: 'Source', width: '10%' },
    { name: 'Hostname', width: '15%' },
    { name: 'IP Address', width: '10%' },
    { name: 'OS Type', width: '10%' },
    { name: 'OS Name', width: '10%' },
    { name: 'OS Version', width: '10%' },
    { name: 'Last Seen', width: '15%' },
    { name: 'Defender Coverage', width: '10%' },
    { name: 'Intune Coverage', width: '10%' }
  ];

  const renderSearchFilter = (
    searchValue: string, 
    setSearchValue: React.Dispatch<React.SetStateAction<string>>,
    searchColumn: string,
    setSearchColumn: React.Dispatch<React.SetStateAction<string>>,
    isSelectOpen: boolean,
    setSelectOpen: React.Dispatch<React.SetStateAction<boolean>>,
    columns: string[],
    filters: { [key: string]: string },
    setFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>
  ) => {
    const attributeToggle = (
      <MenuToggle
        ref={attributeToggleRef}
        onClick={onAttributeToggleClick}
        isExpanded={isAttributeMenuOpen}
        icon={<FilterIcon />}
      >
        {activeAttributeMenu}
      </MenuToggle>
    );

    const attributeMenu = (
      <Menu
        ref={attributeMenuRef}
        onSelect={(_ev, itemId) => {
          const selectedColumn = itemId?.toString() || columns[0];
          setActiveAttributeMenu(selectedColumn);
          setSearchColumn(selectedColumn);
          setSearchValue('');
          setIsAttributeMenuOpen(false);
        }}
      >
        <MenuContent>
          <MenuList>
            {columns.map((column) => (
              <MenuItem key={column} itemId={column}>{column}</MenuItem>
            ))}
          </MenuList>
        </MenuContent>
      </Menu>
    );

    const attributeDropdown = (
      <div ref={attributeContainerRef}>
        <Popper
          trigger={attributeToggle}
          triggerRef={attributeToggleRef}
          popper={attributeMenu}
          popperRef={attributeMenuRef}
          appendTo={attributeContainerRef.current || undefined}
          isVisible={isAttributeMenuOpen}
        />
      </div>
    );

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      if (value.trim()) {
        setFilters(prev => ({
          ...prev,
          [searchColumn]: value.trim()
        }));
      } else {
        // Remove the filter for this column if the search value is empty
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters[searchColumn];
          return newFilters;
        });
      }
    };

    const onDelete = (category: string, id: string) => {
      if (category === '') {
        // Clear all filters
        setFilters({});
        setSearchValue('');
      } else {
        // Remove specific filter
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters[category];
          return newFilters;
        });
        if (category === searchColumn) {
          setSearchValue('');
        }
      }
      setSearchColumn(columns[0]);
      setActiveAttributeMenu(columns[0]);
    };

    const onDeleteGroup = () => {
      setFilters({});
      setSearchValue('');
      setSearchColumn(columns[0]);
      setActiveAttributeMenu(columns[0]);
    };

    return (
      <Toolbar
        id="attribute-search-filter-toolbar"
        style={{ paddingLeft: '1rem' }}
        customLabelGroupContent={<></>}
      >
        <ToolbarContent>
          <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
            <ToolbarGroup variant="filter-group">
              <ToolbarItem>{attributeDropdown}</ToolbarItem>
              <ToolbarFilter
                labels={Object.entries(filters).map(([column, value]) => `${column}: ${value}`)}
                deleteLabel={(category, label) => {
                  const column = label.toString().split(':')[0].trim();
                  onDelete(column, label.toString());
                }}
                deleteLabelGroup={onDeleteGroup}
                categoryName="Filters"
              >
                <SearchInput
                  placeholder={`Search ${searchColumn.toLowerCase()}...`}
                  value={searchValue}
                  onChange={(_event, value) => handleSearchChange(value)}
                  onClear={() => {
                    setSearchValue('');
                    setFilters(prev => {
                      const newFilters = { ...prev };
                      delete newFilters[searchColumn];
                      return newFilters;
                    });
                  }}
                  style={{ width: '300px' }}
                />
              </ToolbarFilter>
            </ToolbarGroup>
          </ToolbarToggleGroup>
          <ToolbarItem variant="pagination">{renderPagination('top')}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>
    );
  };

  const countEq = (field: string, value: string) => fileData.filter((row) => row[field] === value).length;

  const renderReportCharts = () => {
    if (normalizedReportType.includes('active accounts')) {
      return (
        <ReportDonut
          ariaDesc="Account MFA Compliance"
          ariaTitle="Account MFA Compliance"
          name="chart1"
          title={String(fileData.length)}
          subTitle="Active Accounts"
          slices={[
            { x: 'MFA Disabled', y: countEq('MFAStatus', 'FALSE'), color: 'red' },
            { x: 'MFA Enabled', y: countEq('MFAStatus', 'TRUE'), color: 'green' },
          ]}
        />
      );
    }
    if (normalizedReportType.includes('account findings')) {
      return (
        <ReportDonut
          ariaDesc="Account findings by priority"
          ariaTitle="Account findings by priority"
          name="chart1"
          title={String(fileData.length)}
          subTitle="Account Findings"
          slices={prioritySlices(fileData)}
        />
      );
    }
    if (normalizedReportType.includes('environment findings')) {
      return (
        <ReportDonut
          ariaDesc="Environment findings by priority"
          ariaTitle="Environment findings by priority"
          name="chart1"
          title={String(fileData.length)}
          subTitle="Environment Findings"
          slices={prioritySlices(fileData)}
        />
      );
    }
    if (normalizedReportType.includes('asset findings')) {
      return (
        <ReportDonut
          ariaDesc="Asset findings by priority"
          ariaTitle="Asset findings by priority"
          name="chart1"
          title={String(fileData.length)}
          subTitle="Asset Findings"
          slices={prioritySlices(fileData)}
        />
      );
    }
    if (normalizedReportType.includes('detected assets')) {
      return (
        <div className="report-charts">
          <ReportDonut
            ariaDesc="Assets covered by Defender"
            ariaTitle="Assets covered by Defender"
            name="chart1"
            title={String(fileData.length)}
            subTitle="Detected Assets"
            slices={coverageSlices(fileData, 'DefenderCoverage', 'Defender')}
          />
          <ReportDonut
            ariaDesc="Assets covered by Intune"
            ariaTitle="Assets covered by Intune"
            name="chart2"
            title={String(fileData.length)}
            subTitle="Detected Assets"
            slices={coverageSlices(fileData, 'IntuneCoverage', 'Intune')}
          />
        </div>
      );
    }
    return null;
  };

  const renderReportData = () => {
    if (normalizedReportType.includes('active accounts')) {
      return (
        <>
          {renderSearchFilter(
            activeAccountsSearchValue,
            setActiveAccountsSearchValue,
            activeAccountsSearchColumn,
            setActiveAccountsSearchColumn,
            activeAccountsSelectOpen,
            setActiveAccountsSelectOpen,
            activeAccountsColumns.map((col) => col.name),
            activeAccountsFilters,
            setActiveAccountsFilters
          )}
          <ReportDataTable
            ariaLabel="Active Accounts Table"
            columns={activeAccountsColumns}
            sortBy={activeAccountsSortBy}
            onSort={(event, index, direction) => onSort(event, index, direction, setActiveAccountsSortBy)}
          >
            {paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                <Td>{row.Source}</Td>
                <Td>{row.Firstname}</Td>
                <Td>{row.Lastname}</Td>
                <Td>{row.Email}</Td>
                <Td>{row.LastActivity}</Td>
                <Td>{renderLabel(row.MFAStatus)}</Td>
                <Td>{row.PrivilegedRoles}</Td>
              </Tr>
            ))}
          </ReportDataTable>
        </>
      );
    }
    if (normalizedReportType.includes('account findings')) {
      return (
        <>
          {renderSearchFilter(
            accountFindingsSearchValue,
            setAccountFindingsSearchValue,
            accountFindingsSearchColumn,
            setAccountFindingsSearchColumn,
            accountFindingsSelectOpen,
            setAccountFindingsSelectOpen,
            accountFindingsColumns.map((col) => col.name),
            accountFindingsFilters,
            setAccountFindingsFilters
          )}
          <ReportDataTable
            ariaLabel="Account Findings Table"
            columns={accountFindingsColumns}
            sortBy={accountFindingsSortBy}
            onSort={(event, index, direction) => onSort(event, index, direction, setAccountFindingsSortBy)}
          >
            {paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                <Td>{row.Source}</Td>
                <Td>{renderPriority(row.Priority)}</Td>
                <Td>{row.FindingName}</Td>
                <Td>{row.Resource}</Td>
                <Td>{row.Description}</Td>
                <Td>{row.SuggestedRemediation}</Td>
              </Tr>
            ))}
          </ReportDataTable>
        </>
      );
    }
    if (normalizedReportType.includes('environment findings')) {
      return (
        <>
          {renderSearchFilter(
            environmentFindingsSearchValue,
            setEnvironmentFindingsSearchValue,
            environmentFindingsSearchColumn,
            setEnvironmentFindingsSearchColumn,
            environmentFindingsSelectOpen,
            setEnvironmentFindingsSelectOpen,
            environmentFindingsColumns.map((col) => col.name),
            environmentFindingsFilters,
            setEnvironmentFindingsFilters
          )}
          <ReportDataTable
            ariaLabel="Environment Findings Table"
            columns={environmentFindingsColumns}
            sortBy={environmentFindingsSortBy}
            onSort={(event, index, direction) => onSort(event, index, direction, setEnvironmentFindingsSortBy)}
          >
            {paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                <Td>{row.Source}</Td>
                <Td>{renderPriority(row.Priority)}</Td>
                <Td>{row.FindingName}</Td>
                <Td>{row.Resource}</Td>
                <Td>{row.Description}</Td>
                <Td>{row.Remediation}</Td>
                <Td>{row.MoreInfo}</Td>
              </Tr>
            ))}
          </ReportDataTable>
        </>
      );
    }
    if (normalizedReportType.includes('asset findings')) {
      return (
        <>
          {renderSearchFilter(
            assetFindingsSearchValue,
            setAssetFindingsSearchValue,
            assetFindingsSearchColumn,
            setAssetFindingsSearchColumn,
            assetFindingsSelectOpen,
            setAssetFindingsSelectOpen,
            assetFindingsColumns.map((col) => col.name),
            assetFindingsFilters,
            setAssetFindingsFilters
          )}
          <ReportDataTable
            ariaLabel="Asset Findings Table"
            columns={assetFindingsColumns}
            sortBy={assetFindingsSortBy}
            onSort={(event, index, direction) => onSort(event, index, direction, setAssetFindingsSortBy)}
          >
            {paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                <Td>{row.Source}</Td>
                <Td>{renderPriority(row.Priority)}</Td>
                <Td>{row.FindingName}</Td>
                <Td>{row.Resource}</Td>
                <Td>{row.Category}</Td>
                <Td>{row.Description}</Td>
                <Td>{row.SuggestedRemediation}</Td>
              </Tr>
            ))}
          </ReportDataTable>
        </>
      );
    }
    if (normalizedReportType.includes('detected assets')) {
      return (
        <>
          {renderSearchFilter(
            assetsSearchValue,
            setAssetsSearchValue,
            assetsSearchColumn,
            setAssetsSearchColumn,
            assetsSelectOpen,
            setAssetsSelectOpen,
            assetsColumns.map((col) => col.name),
            assetsFilters,
            setAssetsFilters
          )}
          <ReportDataTable
            ariaLabel="Detected Assets Table"
            columns={assetsColumns}
            sortBy={assetsSortBy}
            onSort={(event, index, direction) => onSort(event, index, direction, setAssetsSortBy)}
          >
            {paginatedData.map((row, rowIndex) => (
              <Tr key={rowIndex}>
                <Td>{row.Source}</Td>
                <Td>{row.Hostname}</Td>
                <Td>{row.IPAddress}</Td>
                <Td>{row.OSType}</Td>
                <Td>{row.OSName}</Td>
                <Td>{row.OSVersion}</Td>
                <Td>{row.LastSeen}</Td>
                <Td>{renderLabel(row.DefenderCoverage)}</Td>
                <Td>{renderLabel(row.IntuneCoverage)}</Td>
              </Tr>
            ))}
          </ReportDataTable>
        </>
      );
    }
    return <pre>{JSON.stringify(paginatedData, null, 2)}</pre>;
  };

  const reportCharts = renderReportCharts();

  return (
    <PageSection>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}
      >
        <Title headingLevel="h1" size="xl">
          {selectedFile
            ? `${selectedReportCard?.reportName || 'Report'}${selectedReportTime ? ` - ${selectedReportTime}` : ''}`
            : `${site?.productName || buildTimeBranding.applicationName || 'QuickCheck'} Request History`}
        </Title>
        {selectedFile && (
          <Button variant="secondary" onClick={handleBackClick}>
            Back to Requests list
          </Button>
        )}
      </div>
      
      {(loading || reportLoading) && (
        <Bullseye>
          <EmptyState titleText={reportLoading ? 'Loading report' : 'Loading requests'} headingLevel="h4" icon={Spinner} aria-live="polite">
            <EmptyStateBody>
              {reportLoading
                ? 'Please wait while the report is loaded.'
                : 'Please wait while your requests are loaded.'}
            </EmptyStateBody>
          </EmptyState>
        </Bullseye>
      )}
      
      {error && (
        <Alert
          variant="danger"
          title="Error"
          isInline
          timeout={3000}
          onTimeout={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {!loading && !reportLoading && (
        <>
          {selectedFile ? (
            <>
              <div className="report-overview">
                {reportCharts && (
                  <Card>
                    <CardBody>{reportCharts}</CardBody>
                  </Card>
                )}
                {reportInfo && <ReportInfoCard reportInfo={reportInfo} />}
              </div>
              {renderReportData()}
            </>
          ) : (
            <>
              {files.length === 0 ? (
                <p>No files found in your accessible folders.</p>
              ) : (
                <>
                  <Toolbar
                    id="requests-list-toolbar"
                    clearAllFilters={clearListFilters}
                    collapseListedFiltersBreakpoint="xl"
                  >
                    <ToolbarContent>
                      <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
                        <ToolbarGroup variant="filter-group">
                          <ToolbarItem>
                            <Select
                              isOpen={listAttrOpen}
                              selected={listSearchColumn}
                              onSelect={(_event, value) => {
                                const selected = String(value);
                                setListSearchColumn(selected);
                                setListSearchValue(listFilters[selected] || '');
                                setListAttrOpen(false);
                              }}
                              onOpenChange={setListAttrOpen}
                              toggle={(toggleRef) => (
                                <MenuToggle
                                  ref={toggleRef}
                                  onClick={() => setListAttrOpen(!listAttrOpen)}
                                  isExpanded={listAttrOpen}
                                  icon={<FilterIcon />}
                                >
                                  {listSearchColumn}
                                </MenuToggle>
                              )}
                            >
                              <SelectList>
                                {REQUEST_LIST_FILTER_COLUMNS.map((column) => (
                                  <SelectOption key={column} value={column}>
                                    {column}
                                  </SelectOption>
                                ))}
                              </SelectList>
                            </Select>
                          </ToolbarItem>
                          <ToolbarFilter
                            labels={Object.entries(listFilters).map(([column, value]) => `${column}: ${value}`)}
                            deleteLabel={(_category, label) => {
                              const column = label.toString().split(':')[0].trim();
                              setListFilters((prev) => {
                                const next = { ...prev };
                                delete next[column];
                                return next;
                              });
                              if (column === listSearchColumn) {
                                setListSearchValue('');
                              }
                              setListPage(1);
                            }}
                            deleteLabelGroup={clearListFilters}
                            categoryName="Filters"
                          >
                            <SearchInput
                              aria-label={`Filter by ${listSearchColumn}`}
                              placeholder={`Filter by ${listSearchColumn.toLowerCase()}...`}
                              value={listSearchValue}
                              onChange={(_event, value) => handleListSearchChange(value)}
                              onClear={() => handleListSearchChange('')}
                            />
                          </ToolbarFilter>
                        </ToolbarGroup>
                      </ToolbarToggleGroup>
                      <ToolbarItem variant="pagination">{renderListPagination()}</ToolbarItem>
                    </ToolbarContent>
                  </Toolbar>
                  <OuterScrollContainer className="requests-list-scroll">
                    <InnerScrollContainer>
                      <Table
                        variant="compact"
                        isStickyHeader
                        aria-label="Requests"
                        className="requests-list-table pf-m-fit-content"
                      >
                        <Thead>
                          <Tr>
                            {REQUEST_LIST_FILTER_COLUMNS.map((column, index) => (
                              <Th
                                key={column}
                                sort={{
                                  sortBy: listSortBy,
                                  onSort: (_event, columnIndex, direction) => {
                                    setListSortBy({ index: columnIndex, direction });
                                    setListPage(1);
                                  },
                                  columnIndex: index
                                }}
                              >
                                {column}
                              </Th>
                            ))}
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody style={{ verticalAlign: 'middle' }}>
                          {paginatedListFiles.length === 0 ? (
                            <Tr>
                              <Td colSpan={5}>
                                <Bullseye>
                                  <EmptyState titleText="No matching requests" headingLevel="h4">
                                    <EmptyStateBody>
                                      No requests match the current filters. Clear filters to see all requests.
                                    </EmptyStateBody>
                                  </EmptyState>
                                </Bullseye>
                              </Td>
                            </Tr>
                          ) : (
                            paginatedListFiles.map((file) => {
                              const statusLabel = getRequestStatusLabel(file.state);
                              const requestId = file.requestId || file.filePath;
                              return (
                                <Tr key={file.filePath}>
                                  <Td dataLabel="Type of Check" style={{ verticalAlign: 'middle' }}>{file.reportName}</Td>
                                  <Td dataLabel="Initiated by" style={{ verticalAlign: 'middle' }}>{formatRequestOwner(file)}</Td>
                                  <Td dataLabel="Request time" style={{ verticalAlign: 'middle' }}>{file.lastModified.toLocaleString()}</Td>
                                  <Td dataLabel="Status" style={{ verticalAlign: 'middle' }}>
                                    <Label isCompact status={statusLabel.status}>
                                      {statusLabel.text}
                                    </Label>
                                  </Td>
                                  <Td dataLabel="Actions" isActionCell style={{ verticalAlign: 'middle' }}>
                                    <RequestListActions
                                      file={file}
                                      onViewReport={() => void handleFileClick(file.filePath)}
                                      onStatusLogs={() => navigateToStatus(requestId)}
                                    />
                                  </Td>
                                </Tr>
                              );
                            })
                          )}
                        </Tbody>
                      </Table>
                    </InnerScrollContainer>
                  </OuterScrollContainer>
                </>
              )}
            </>
          )}
        </>
      )}
    </PageSection>
  );
};

export { Requests }; 